/* pure-scroller -- section-scoped horizontal scroller with prev/next arrows and
   an optional dot nav (Webflow `.w-slider-nav`: one dot per slide, the active one
   opaque and the rest at 40%). Reusable across the testimonial + category
   sliders. Idempotent (guarded define), cleans up its listeners on disconnect,
   and honors prefers-reduced-motion. Dots are real <button>s rendered in Liquid,
   so they are keyboard-operable and the layout does not depend on JS. */
if (!customElements.get('pure-scroller')) {
  customElements.define(
    'pure-scroller',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-scroller-track]');
        if (!this.track) return;
        this.prevBtn = this.querySelector('[data-scroller-prev]');
        this.nextBtn = this.querySelector('[data-scroller-next]');
        this.dots = Array.from(this.querySelectorAll('[data-scroller-dot]'));

        this.onPrev = () => this.step(-1);
        this.onNext = () => this.step(1);
        if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);

        if (this.dots.length) {
          this.onDotClick = (event) => {
            const index = Number(event.currentTarget.dataset.scrollerDot);
            if (Number.isNaN(index)) return;
            this.scrollToIndex(index);
          };
          this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

          // rAF-throttled so a flung scroll does not thrash the DOM
          this.onScroll = () => {
            if (this.rafId) return;
            this.rafId = requestAnimationFrame(() => {
              this.rafId = null;
              this.syncDots();
            });
          };
          this.track.addEventListener('scroll', this.onScroll, { passive: true });

          /* A dot click pins the highlight to the dot the user chose, because
             several dots can share one scroll offset. Any hands-on scrolling
             releases that pin so the highlight follows the track again. */
          this.unpin = () => {
            this.pinned = null;
          };
          for (const evt of ['wheel', 'pointerdown', 'touchstart', 'keydown']) {
            this.track.addEventListener(evt, this.unpin, { passive: true });
          }
          this.syncDots();
        }
      }

      disconnectedCallback() {
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
        if (this.dots && this.onDotClick) {
          this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
        }
        if (this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
        if (this.unpin) {
          for (const evt of ['wheel', 'pointerdown', 'touchstart', 'keydown']) {
            this.track.removeEventListener(evt, this.unpin);
          }
        }
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      /* Distance from one slide's leading edge to the next. */
      pitch() {
        const item = this.track.querySelector(':scope > *');
        if (!item) return this.track.clientWidth * 0.8;
        const styles = getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return item.getBoundingClientRect().width + gap;
      }

      behavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      /* A sticky header can cover the testimonial title when carousel
         navigation happens near the top of the viewport. Only correct an
         actual overlap; ordinary carousel navigation must not move the page. */
      revealObscuredHeading() {
        const heading = this.querySelector('.pure-tst__head');
        const stickyHeader = document.querySelector(
          '.section-header.shopify-section-header-sticky'
        );
        if (!heading || !stickyHeader) return;

        const clearance = 16;
        const overlap = stickyHeader.getBoundingClientRect().bottom + clearance -
          heading.getBoundingClientRect().top;
        if (overlap > 0) window.scrollBy({ top: -overlap, behavior: 'auto' });
      }

      step(direction) {
        this.revealObscuredHeading();
        if (this.hasAttribute('data-scroller-slide-navigation') && this.dots.length) {
          const current = this.pinned ?? Math.round(this.track.scrollLeft / this.pitch());
          this.scrollToIndex(Math.max(0, Math.min(current + direction, this.dots.length - 1)));
          return;
        }
        this.pinned = null;
        this.track.scrollBy({ left: direction * this.pitch(), behavior: this.behavior() });
      }

      /* Where the track has to sit for slide `index` to lead. With three slides in
         view and five in the track only the first three positions are reachable,
         so the tail indices all clamp to the same offset. */
      targetFor(index) {
        const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        return Math.max(0, Math.min(index * this.pitch(), max));
      }

      scrollToIndex(index) {
        // Remember the intent: several dots can share one scroll offset, so
        // position alone cannot tell us which one the user picked.
        this.pinned = index;
        this.track.scrollTo({ left: this.targetFor(index), behavior: this.behavior() });
        this.syncDots();
      }

      syncDots() {
        if (!this.pitch()) return;
        const left = this.track.scrollLeft;
        let active;
        if (this.pinned != null) {
          active = this.pinned;
        } else {
          active = 0;
          let best = Infinity;
          this.dots.forEach((_, i) => {
            const d = Math.abs(left - this.targetFor(i));
            if (d < best - 0.5) {
              best = d;
              active = i;
            }
          });
        }
        this.dots.forEach((dot, i) => {
          const isActive = i === active;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
